"use client";

// ─── Pływająca ikona koszyka (Floating Cart) ─────────────────────
// Widoczna w prawym dolnym rogu, gdy w koszyku jest min. 1 produkt.
// Ukrywana automatycznie, gdy koszyk jest pusty lub gdy panel koszyka
// jest otwarty. Działa na desktopie i mobile, obsługuje klawiaturę i
// czytniki ekranu. Licznik aktualizuje się w czasie rzeczywistym.

import { AnimatePresence, motion } from "framer-motion";
import { ShoppingCart } from "lucide-react";
import { usePathname } from "next/navigation";
import { useCart } from "@/lib/cart";

export default function FloatingCart() {
  const { count, totalPrice, isOpen, openCart } = useCart();
  const pathname = usePathname();

  // Ukryj pływający koszyk w widoku nauki lekcji (/kurs/[slug]/lekcja/[lessonId]).
  const hiddenOnLessonView = /^\/kurs\/[^/]+\/lekcja\//.test(pathname ?? "");

  const visible = count > 0 && !isOpen && !hiddenOnLessonView;

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          key="floating-cart"
          initial={{ opacity: 0, scale: 0.6, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.6, y: 20 }}
          transition={{ type: "spring", stiffness: 320, damping: 22 }}
          className="fixed bottom-5 right-5 z-[55] sm:bottom-6 sm:right-6"
          style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
        >
          <motion.button
            type="button"
            onClick={openCart}
            whileHover={{ scale: 1.06 }}
            whileTap={{ scale: 0.94 }}
            aria-label={`Otwórz koszyk — ${count} ${count === 1 ? "produkt" : "produkty"}`}
            className="group relative flex items-center gap-2.5 rounded-full bg-primary py-3 pl-3.5 pr-4 text-white shadow-xl shadow-primary/30 outline-none transition-colors hover:bg-primary/90 focus-visible:ring-4 focus-visible:ring-primary/30"
          >
            <span className="relative flex h-7 w-7 items-center justify-center">
              <ShoppingCart size={22} />
              {/* Licznik produktów */}
              <AnimatePresence mode="popLayout">
                <motion.span
                  key={count}
                  initial={{ scale: 0, y: -4 }}
                  animate={{ scale: 1, y: 0 }}
                  exit={{ scale: 0, opacity: 0 }}
                  transition={{ type: "spring", stiffness: 500, damping: 22 }}
                  className="absolute -right-2 -top-2 flex h-5 min-w-[20px] items-center justify-center rounded-full bg-amber-400 px-1 text-[11px] font-extrabold leading-none text-slate-900 ring-2 ring-white"
                >
                  {count}
                </motion.span>
              </AnimatePresence>
            </span>

            {/* Etykieta z kwotą — rozwijana na desktopie */}
            <span className="hidden text-sm font-bold sm:inline">
              {totalPrice > 0 ? `${totalPrice.toFixed(0)} zł` : "Koszyk"}
            </span>
          </motion.button>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
